// Trends. Big line chart for overall reading score + 6 small multiples for
// each dimension. Window selector toggles the underlying patterns/listTrends
// fetch.
//
// Replaces the old Calendar route's role as the "longitudinal" surface.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  getPatterns,
  listTrends,
  SessionExpiredError,
  type DimensionPattern,
  type PatternsResponse,
  type TrendItem,
} from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { TrendChart, type TrendPoint } from "../components/TrendChart";
import { DimensionMultiple } from "../components/DimensionMultiple";

type Window = "7d" | "30d" | "6w" | "all";

const WINDOW_OPTIONS: ReadonlyArray<{ value: Window; label: string; days: number }> = [
  { value: "7d", label: "7d", days: 7 },
  { value: "30d", label: "30d", days: 30 },
  { value: "6w", label: "6w", days: 42 },
  { value: "all", label: "All", days: 365 },
];

const DIMENSION_LABEL: Record<DimensionPattern["dimension"], string> = {
  clarity: "Clarity",
  confidence: "Confidence",
  conciseness: "Conciseness",
  listening: "Listening",
  structure: "Structure",
  pacing: "Pacing",
};

const DIMENSION_ORDER: DimensionPattern["dimension"][] = [
  "clarity",
  "confidence",
  "conciseness",
  "listening",
  "structure",
  "pacing",
];

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; trends: TrendItem[]; patterns: PatternsResponse }
  | { kind: "error"; message: string };

export function Trends() {
  const navigate = useNavigate();
  const [window, setWindow] = useState<Window>("6w");
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const days = WINDOW_OPTIONS.find((w) => w.value === window)?.days ?? 30;

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const [trends, patterns] = await Promise.all([
        listTrends({ limit: 100 }),
        getPatterns({ days }),
      ]);
      setState({ kind: "ok", trends: trends.items, patterns });
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        navigate("/login", { replace: true });
        return;
      }
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }, [days, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const pointsAndRange = useMemo(() => {
    if (state.kind !== "ok") return null;
    const cutoff = Math.floor(Date.now() / 1000) - days * 86400;
    const filtered = state.trends
      .filter((t) => typeof t.overallScore === "number" && t.createdAt >= cutoff)
      .slice()
      .sort((a, b) => a.createdAt - b.createdAt);
    return computePoints(filtered);
  }, [state, days]);

  return (
    <>
      <Crumbs trail={["Trends"]} />
      <div className="page-h">
        <div>
          <h1>Trends.</h1>
          <p className="sub">How your reading score and the six dimensions have moved. Sage dot is a personal best, amber is flagged.</p>
        </div>
        <div className="actions">
          <div className="segm" role="tablist" aria-label="Window">
            {WINDOW_OPTIONS.map((w) => (
              <button
                key={w.value}
                type="button"
                className={w.value === window ? "on" : ""}
                onClick={() => setWindow(w.value)}
                aria-pressed={w.value === window}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {state.kind === "loading" ? <LoadingPanel /> : null}
      {state.kind === "error" ? (
        <p style={{ color: "rgb(var(--color-warning))" }}>{state.message}</p>
      ) : null}

      {state.kind === "ok" && pointsAndRange ? (
        <>
          <section className="kpanel" style={{ marginBottom: 14, paddingBottom: 14 }}>
            <div className="kpanel-h">
              <h3>Overall reading score</h3>
              <span className="meta">
                {pointsAndRange.points.length} reading{pointsAndRange.points.length === 1 ? "" : "s"}
                {pointsAndRange.points.length >= 2
                  ? ` · ${formatDate(pointsAndRange.points[0]!.date)} – ${formatDate(
                      pointsAndRange.points[pointsAndRange.points.length - 1]!.date,
                    )}`
                  : ""}
              </span>
            </div>
            <TrendChart
              points={pointsAndRange.points}
              yMin={pointsAndRange.yMin}
              yMax={pointsAndRange.yMax}
            />
          </section>

          {state.patterns.meetingCount >= 3 ? (
            <section className="smalls">
              {DIMENSION_ORDER.map((dim) => {
                const p = state.patterns.perDimension.find((d) => d.dimension === dim);
                if (!p) {
                  return (
                    <DimensionMultiple
                      key={dim}
                      name={DIMENSION_LABEL[dim]}
                      score={0}
                      deltaVsPrior={null}
                      trajectory={[]}
                      trendLabel="stable"
                      sub="No data yet"
                    />
                  );
                }
                const deltaVsPrior = computeDelta(p.trajectory);
                return (
                  <DimensionMultiple
                    key={dim}
                    name={DIMENSION_LABEL[dim]}
                    score={p.averageScore}
                    deltaVsPrior={deltaVsPrior}
                    trajectory={p.trajectory.map((t) => t.score)}
                    trendLabel={p.trendLabel}
                    sub={trendSub(p.trendLabel)}
                  />
                );
              })}
            </section>
          ) : (
            <div className="kit-empty">
              Bring 3+ readings to see per-dimension trends.
              <div style={{ marginTop: 14 }}>
                <Link to="/new" className="btn btn-primary">Bring a transcript</Link>
              </div>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}

function LoadingPanel() {
  return (
    <div className="kpanel" style={{ marginBottom: 14, opacity: 0.5 }} aria-hidden="true">
      <div className="kpanel-h">
        <h3>Overall reading score</h3>
        <span className="meta">Loading…</span>
      </div>
      <div style={{ height: 240 }} />
    </div>
  );
}

function computePoints(items: ReadonlyArray<TrendItem & { overallScore: number | null }>): { points: TrendPoint[]; yMin: number; yMax: number } | null {
  if (items.length < 1) return { points: [], yMin: 45, yMax: 90 };
  const scores = items.map((i) => i.overallScore as number);
  const max = Math.max(...scores);
  const min = Math.min(...scores);
  // Pad y-range a touch so dots don't kiss the axes.
  const pad = Math.max(5, (max - min) * 0.15);
  const yMin = Math.max(0, Math.floor((min - pad) / 5) * 5);
  const yMax = Math.min(100, Math.ceil((max + pad) / 5) * 5);

  const peakScore = max;
  const points: TrendPoint[] = items.map((it, i) => {
    const score = it.overallScore as number;
    let flag: TrendPoint["flag"];
    if (score === peakScore && items.length >= 3) flag = "peak";
    else if (i > 0) {
      // Flag if this point dropped >=10 from prior.
      const prior = items[i - 1]!.overallScore as number;
      if (prior - score >= 10) flag = "flag";
    }
    return { date: it.createdAt, score, flag };
  });

  return { points, yMin, yMax };
}

function computeDelta(trajectory: ReadonlyArray<{ score: number }>): number | null {
  if (trajectory.length < 2) return null;
  const half = Math.floor(trajectory.length / 2);
  const earlier = trajectory.slice(0, half);
  const later = trajectory.slice(half);
  const avg = (xs: ReadonlyArray<{ score: number }>) =>
    xs.reduce((acc, x) => acc + x.score, 0) / Math.max(xs.length, 1);
  return avg(later) - avg(earlier);
}

function trendSub(trend: "rising" | "falling" | "stable"): string {
  if (trend === "rising") return "Trending up";
  if (trend === "falling") return "Trending down";
  return "Holding steady";
}

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
